/* The keys panel: a plain list over the world, opened by `?` and closed by Esc or its Close button. It is not a dialog and it
   takes no focus away for good: closing it returns focus to where the reader was. useKeys is the one window listener for the
   keys that are not the command line's own. */
import { useCallback, useEffect, useRef, useState } from 'react'
import { SHORTCUTS, actionFor, isTyping } from '../model/shortcuts.ts'

/** Closes the open <details> that holds the focus; true when it closed one. */
function closeHeldSection(): boolean {
  const held = document.activeElement?.closest('details[open]')
  if (!held) return false
  held.removeAttribute('open')
  held.querySelector<HTMLElement>('summary')?.focus()
  return true
}

export function useKeys() {
  const [open, setOpen] = useState(false)
  const from = useRef<HTMLElement | null>(null)
  const panel = useRef<HTMLElement>(null)

  const show = useCallback(() => {
    from.current = document.activeElement instanceof HTMLElement ? document.activeElement : null
    setOpen(true)
  }, [])
  const hide = useCallback(() => {
    setOpen(false)
    const back = from.current
    from.current = null
    if (back && document.contains(back)) back.focus()
  }, [])

  useEffect(() => {
    if (open) panel.current?.focus()
  }, [open])

  useEffect(() => {
    const on = (e: KeyboardEvent) => {
      const act = actionFor({ key: e.key, metaKey: e.metaKey, ctrlKey: e.ctrlKey, altKey: e.altKey, typing: isTyping(e.target) })
      if (act === 'keys') {
        e.preventDefault()
        if (open) hide()
        else show()
      } else if (act === 'close') {
        // The command line clears itself on Esc; the keys close first, then the section the focus is in. The frame (Chrome.tsx) leaves the page only when neither is open.
        if (document.activeElement?.id === 'command-line') return
        if (open) {
          e.preventDefault()
          hide()
        } else if (closeHeldSection()) e.preventDefault()
      }
    }
    window.addEventListener('keydown', on)
    return () => window.removeEventListener('keydown', on)
  }, [open, show, hide])

  return { open, show, hide, panel }
}

export function KeysPanel({ panel, onClose }: { panel: React.RefObject<HTMLElement | null>; onClose: () => void }) {
  return (
    <section id="keys-panel" ref={panel} tabIndex={-1} className="keys-panel" aria-labelledby="keys-title">
      <div className="flex flex-wrap items-baseline gap-2">
        <h2 id="keys-title" style={{ flex: 1 }}>Keys</h2>
        <button type="button" className="btn btn-sm btn-ghost" onClick={onClose}>
          Close <span className="kbd" aria-hidden="true">Esc</span>
        </button>
      </div>
      <dl className="keys-list">
        {SHORTCUTS.map((s) => (
          <div key={s.keys.join('|')}>
            <dt>
              {s.keys.map((k, i) => (
                <span key={k}>
                  {i > 0 ? <span className="faint"> or </span> : null}
                  <kbd className="kbd">{k}</kbd>
                </span>
              ))}
            </dt>
            <dd>{s.does}</dd>
          </div>
        ))}
      </dl>
    </section>
  )
}
